/* ==========================================================================
   boyin-brand.js
   Beauty By Boyin — two global behaviours only. Loaded deferred.

   1. Header: transparent over the hero, solid once scrolled past it.
   2. Fade-ins: reveals `.boyin-fade` elements on scroll.

   No dependencies, no jQuery. Everything degrades to "visible and usable"
   if JS fails: `.boyin-fade` is the only hidden state and it is force-shown
   on load when IntersectionObserver is unavailable.
   ========================================================================== */

(function () {
  'use strict';

  /* ---- 1. Header scroll state --------------------------------------------
     Only runs on templates that opted into a transparent header by adding
     `boyin-header-transparent` to <body>. Threshold is the hero height so the
     swap lands exactly as the hero leaves, not at an arbitrary pixel value. */
  function initHeader() {
    var body = document.body;
    if (!body.classList.contains('boyin-header-transparent')) return;

    var hero = document.querySelector('[data-boyin-hero]');
    var ticking = false;

    // Publish the header's height so the CSS can pull the hero up underneath it
    // by exactly the right amount. Re-measured on resize, since the header
    // changes height across breakpoints.
    var headerEl = document.querySelector('.header-wrapper');

    function measureHeader() {
      if (!headerEl) return;
      document.documentElement.style.setProperty(
        '--boyin-header-h',
        headerEl.offsetHeight + 'px'
      );
    }

    measureHeader();
    window.addEventListener('resize', measureHeader, { passive: true });

    function threshold() {
      // Fall back to viewport height if the hero section isn't on this page.
      return hero ? Math.max(hero.offsetHeight - 80, 80) : window.innerHeight * 0.6;
    }

    function update() {
      body.classList.toggle('boyin-header--scrolled', window.scrollY > threshold());
      ticking = false;
    }

    // rAF-throttled: one class check per frame at most, never per scroll event.
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          window.requestAnimationFrame(update);
        }
      },
      { passive: true }
    );

    update();
  }

  /* ---- 2. Fade-in on scroll ----------------------------------------------
     The house has exactly two motion behaviours. This is the first: fade plus
     a 22px rise, 0.9s, triggered once per element by an IntersectionObserver
     at threshold 0.12 with rootMargin '0px 0px -50px 0px'. The other is the
     card hover lift, which is pure CSS.

     Two guards on top of the plain observer, both for the same failure: IO
     notifies on threshold *crossings*, so an element that reaches a position
     above the viewport without ever being reported as intersecting — an
     anchor jump, a restored scroll position, a fast flick — would sit at
     opacity 0 for good. So (a) any entry whose box has ended up past the top
     counts as a reveal, and (b) a rAF-throttled sweep watches for the same
     condition and detaches itself once every element has been shown. */
  function initFades() {
    var items = [].slice.call(document.querySelectorAll('.boyin-fade:not(.boyin-in)'));
    if (!items.length) return;

    function show(el) {
      el.classList.add('boyin-in');
    }

    // Reduced motion → show everything immediately, no observer at all.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !window.IntersectionObserver) {
      items.forEach(show);
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting || entry.boundingClientRect.bottom < 0) {
            reveal(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -50px 0px' }
    );

    function reveal(el) {
      show(el);
      io.unobserve(el);
      pending = pending.filter(function (other) {
        return other !== el;
      });
      if (!pending.length) detachSweep();
    }

    /* The safety net for the failure this theme hit once before: IO only
       notifies on threshold *crossings*, so an element that goes from below
       the viewport to above it inside a single frame — an anchor jump, a
       restored scroll position, a fast flick — is never reported and would
       stay at opacity 0 forever. This sweep reveals anything that has ended up
       past the top, and detaches itself the moment nothing is left. */
    var pending = items.slice();

    function sweep() {
      pending.slice().forEach(function (el) {
        if (el.getBoundingClientRect().bottom < 0) reveal(el);
      });
    }

    function onScroll() {
      window.requestAnimationFrame(sweep);
    }

    function detachSweep() {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('hashchange', onScroll);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('hashchange', onScroll);

    items.forEach(function (el) {
      io.observe(el);
    });
  }

  function init() {
    initHeader();
    initFades();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders sections without a page reload — re-scan for new
  // .boyin-fade nodes so freshly added sections aren't stuck invisible.
  document.addEventListener('shopify:section:load', initFades);
})();
